/* eslint-disable no-bitwise -- intentional bitwise operations for UUID/device-id generation */
import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { CookiesService } from '../cookie/cookie.service';
import { DEVICE_ID_COOKIE_DOMAIN } from './device-id-cookie-domain.token';

/**
 * A lot of this is due to the trixel cookie being set as HTTP only
 * It is no longer, but we need to cater for that.
 * Trixel cookies last a year, so we should be able to ignore this after 01/01/2026
 */
@Injectable()
export class DeviceIdentificationService {
  private readonly cookiesService = inject(CookiesService);
  private readonly document = inject(DOCUMENT);
  private readonly configuredDomain = inject(DEVICE_ID_COOKIE_DOMAIN, { optional: true });

  private readonly trixelsCookie = 'DeviceId';
  private readonly fallBackCookie = 'FallbackDeviceId';

  /** A year, matching Trixel's own cookie. */
  private readonly fallBackCookieLifetimeMs = 365 * 24 * 60 * 60 * 1000;

  private deviceId?: string;

  public getDeviceId(): string {
    if (this.deviceId) {
      return this.deviceId;
    }
    const cookieId = this.readFromCookie();
    if (cookieId) {
      this.deviceId = cookieId;
      return this.deviceId;
    }

    const uuid = this.generateUuid();

    // Dropping either silently breaks cross-subdomain identity: a path-less cookie is scoped to the
    // directory that minted it, and an expiry-less one dies with the tab.
    this.cookiesService.put(this.fallBackCookie, uuid, {
      domain: this.apexCookieDomain(),
      path: '/',
      expires: new Date(Date.now() + this.fallBackCookieLifetimeMs),
    });
    this.deviceId = uuid;

    return this.deviceId;
  }

  private readFromCookie(): string | undefined {
    return this.cookiesService.get(this.trixelsCookie) || this.readFallbackCookie();
  }

  /** Whole jar rather than by name: mid-migration both cookies exist, and the apex one is last. */
  private readFallbackCookie(): string | undefined {
    return this.cookiesService.getAll()[this.fallBackCookie] || undefined;
  }

  /** Dropped where the page isn't served from that domain, because the browser would reject it. */
  private apexCookieDomain(): string | undefined {
    const host = this.document.location?.hostname;

    if (!this.configuredDomain || !host) {
      return undefined;
    }

    return host === this.configuredDomain || host.endsWith(`.${this.configuredDomain}`) ? this.configuredDomain : undefined;
  }

  private generateUuid(): string {
    return crypto.randomUUID ? crypto.randomUUID() : this.fallbackGenerateUuid();
  }

  /**
   * crypto.randomUUID has 95% support
   */
  private fallbackGenerateUuid(): string {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (+c ^ ((crypto.getRandomValues(new Uint8Array(1))[0] ?? 0) & (15 >> (+c / 4)))).toString(16),
    );
  }
}
